import { useState, useEffect } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { useAppDispatch, useAppSelector } from "../redux/hooks"
import { resetPassword, clearError } from "../redux/slices/authSlice"
import { errorMessage } from "../utils/mutationResult"
import { toast } from "../hooks/use-toast"
import { Button } from "../components/ui/button"
import { Input } from "../components/ui/input"
import { Label } from "../components/ui/label"
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card"
import { Alert, AlertDescription } from "../components/ui/alert"
import { ArrowLeft, Lock, Loader2, CheckCircle, Eye, EyeOff, WifiOff } from "lucide-react"

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get("token")
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const [newPassword, setNewPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [showNew, setShowNew] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [localError, setLocalError] = useState("")
  const [success, setSuccess] = useState(false)

  const { loading, error } = useAppSelector((state) => state.auth)
  // Resetting the password always requires a live request, so surface the
  // offline state up front instead of letting the user submit into a
  // request that can never complete.
  const isOnline = useAppSelector((state) => state.offline?.isOnline ?? true)

  useEffect(() => {
    dispatch(clearError())
  }, [dispatch])

  const showLocalError = (message) => {
    setLocalError(message)
    toast.error("Unable to reset password", message)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLocalError("")
    dispatch(clearError())

    if (!isOnline) {
      showLocalError("You're offline. Reconnect to the internet to reset your password.")
      return
    }
    if (newPassword.length < 6) {
      showLocalError("Password must be at least 6 characters.")
      return
    }
    if (newPassword !== confirmPassword) {
      showLocalError("Passwords do not match.")
      return
    }
    if (!token) {
      showLocalError("Invalid or missing reset token. Please request a new link.")
      return
    }

    try {
      await dispatch(resetPassword({ resetToken: token, newPassword })).unwrap()
      setSuccess(true)
      setTimeout(() => navigate("/"), 3000)
    } catch (err) {
      toast.error("Unable to reset password", errorMessage(err, "Password reset failed"))
    }
  }

  const displayError = localError || (error ? errorMessage(error, "Password reset failed") : "")

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:bg-none dark:bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2 dark:text-foreground">EncoAudit</h1>
          <p className="text-slate-600 dark:text-muted-foreground">Set a new password</p>
        </div>

        <Card className="shadow-lg border-0 bg-white/80 backdrop-blur-sm">
          <CardHeader className="text-center pb-4">
            <div className="mx-auto w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
              {success ? (
                <CheckCircle className="w-6 h-6 text-green-600" />
              ) : (
                <Lock className="w-6 h-6 text-blue-600 dark:text-link" />
              )}
            </div>
            <CardTitle className="text-xl font-semibold text-slate-900 dark:text-foreground">
              {success ? "Password Reset" : "Reset Password"}
            </CardTitle>
          </CardHeader>

          <CardContent>
            {success ? (
              <div className="space-y-4 text-center">
                <p className="text-sm text-slate-600 dark:text-muted-foreground">
                  Your password has been reset successfully. You will be redirected to the sign in page in a moment.
                </p>
                <Link to="/">
                  <Button className="w-full h-11 bg-blue-600 dark:bg-primary dark:hover:bg-primary-hover dark:ring-1 dark:ring-inset dark:ring-primary-border hover:bg-blue-700 text-white font-medium">
                    Sign In Now
                  </Button>
                </Link>
              </div>
            ) : !token ? (
              <div className="space-y-4 text-center">
                <Alert variant="destructive">
                  <AlertDescription>
                    This reset link is invalid or has expired. Please request a new one.
                  </AlertDescription>
                </Alert>
                <Link to="/forgot-password">
                  <Button variant="outline" className="w-full h-11">
                    Request New Link
                  </Button>
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="newPassword" className="text-sm font-medium text-slate-700 dark:text-foreground">
                    New Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="newPassword"
                      type={showNew ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password"
                      required
                      minLength={6}
                      className="h-11 pr-10"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setShowNew((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:text-muted-foreground"
                      tabIndex={-1}
                      aria-label={showNew ? "Hide password" : "Show password"}
                    >
                      {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-sm font-medium text-slate-700 dark:text-foreground">
                    Confirm New Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirm ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                      required
                      minLength={6}
                      className="h-11 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:text-muted-foreground"
                      tabIndex={-1}
                      aria-label={showConfirm ? "Hide password" : "Show password"}
                    >
                      {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {!isOnline && (
                  <Alert variant="destructive">
                    <WifiOff className="h-4 w-4" />
                    <AlertDescription>You&apos;re offline. Reconnect to the internet to reset your password.</AlertDescription>
                  </Alert>
                )}

                {isOnline && displayError && (
                  <Alert variant="destructive">
                    <AlertDescription>{displayError}</AlertDescription>
                  </Alert>
                )}

                <Button
                  type="submit"
                  className="w-full h-11 bg-blue-600 dark:bg-primary dark:hover:bg-primary-hover dark:ring-1 dark:ring-inset dark:ring-primary-border hover:bg-blue-700 text-white font-medium"
                  disabled={loading || !isOnline}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Resetting...
                    </>
                  ) : !isOnline ? (
                    <>
                      <WifiOff className="w-4 h-4 mr-2" />
                      Offline
                    </>
                  ) : (
                    "Reset Password"
                  )}
                </Button>
              </form>
            )}

            {!success && (
              <div className="mt-4 text-center">
                <Link
                  to="/"
                  className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 dark:text-muted-foreground"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  Back to Sign In
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default ResetPasswordPage
